import { Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { StudentStatusPageShell } from '../../components/dashboard/StudentStatusPageShell';
import { formatDate } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function ActiveStudentsPage() {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setSignups([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextSignups, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setSignups(nextSignups);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setSignups([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const rows = useMemo(() => {
    const search = normalizeText(searchTerm);

    return signups
      .filter((item) => normalizeText(item.status) === 'student')
      .map((item) => ({
        ...item,
        courseName: item.courseName || courseLookup.get(item.courseId)?.name || '-',
      }))
      .filter((item) => {
        if (!search) return true;
        return [
          formatStudentName(item),
          item.courseName,
          item.intakeLabel,
          item.email,
          item.phone,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => {
        const left = toDate(a.admissionDate || a.admittedAt || a.createdAt) || new Date(0);
        const right = toDate(b.admissionDate || b.admittedAt || b.createdAt) || new Date(0);
        return right - left;
      });
  }, [courseLookup, searchTerm, signups]);

  const studentCount = signups.filter((item) => normalizeText(item.status) === 'student').length;
  const insightCards = [
    { label: 'Learners in motion', value: studentCount, note: 'Admitted students currently learning.', tone: 'primary' },
    { label: 'Learning paths', value: courses.length, note: 'Courses carrying your active learners.', tone: 'blue' },
    { label: 'Roster view', value: rows.length, note: 'Students matching the current search.', tone: 'green' },
  ];

  if (loading) {
    return <LoadingState label="Loading active students..." />;
  }

  return (
    <StudentStatusPageShell
      title="Active Students"
      description="Students who have been admitted from applications and are now active in the academy."
      kicker="Students"
      stats={[]}
      insightCards={insightCards}
      className="active-students-page"
      tableTitle="Active Students"
      tableMeta={`${rows.length} admitted students`}
      tableDescription="Students who have been admitted and are currently active."
      searchId="active-students-search"
      searchValue={searchTerm}
      onSearchChange={(event) => setSearchTerm(event.target.value)}
      searchPlaceholder="Search student, course, cohort..."
      rows={rows}
      columns={[
        {
          key: 'student',
          label: 'Student',
          render: (item) => (
            <div className="appsStudentCell">
              <div className="application-avatar" aria-hidden="true">
                {String(formatStudentName(item)).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ST'}
              </div>
              <div className="application-applicant-copy">
                <strong>{formatStudentName(item)}</strong>
                <span>{item.intakeLabel || 'Open intake'}</span>
              </div>
            </div>
          ),
        },
        { key: 'courseName', label: 'Course', render: (item) => item.courseName || '-' },
        { key: 'intakeLabel', label: 'Cohort', render: (item) => item.intakeLabel || 'Open intake' },
        {
          key: 'admitted',
          label: 'Admitted',
          render: (item) => formatDate(item.admissionDate || item.admittedAt || item.createdAt),
        },
        {
          key: 'view',
          label: 'View student',
          render: (item) => (
            <Button to={`/view-student?applicationId=${encodeURIComponent(item.id)}`} variant="secondary" className="appsViewBtn">
              View
            </Button>
          ),
        },
      ]}
      empty={(
        <EmptyState
          title="No active students yet."
          message="Admitted applicants will appear here after you click Admit from the applications page."
          icon={Users}
        />
      )}
    />
  );
}
